import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { WORKSITEALL, COMPANY, PROJECT, WORKSITE, PROGRESS } from "../../services/actions";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import Swal from "sweetalert2";

import {
  Container,
  Box,
  Button,
  Card,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Slider
} from "@mui/material";
import ActionBar from "../extra/ActionBar";

import Timeline from "./Timeline";
import {
  BsFillPencilFill,
  BsTrash3,
  BsBuilding,
  BsFolder2Open,
  BsCheckCircleFill,
  BsClockHistory,
  BsExclamationTriangleFill,
  BsListTask,
  BsChevronLeft,
  BsChevronRight,
  BsSearch,
  BsCalendar3,
  BsPlusLg
} from "react-icons/bs";
import "./timeline.css";

export default function View() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { from_project } = location.state || {};

  const [project, setProject] = useState({});
  const [company, setCompany] = useState(0);
  const [projects, setProjects] = useState([]);
  const [days, setDays] = useState({});
  const [daysOffset, setDaysOffset] = useState(0);
  const [windowSize, setWindowSize] = useState(14);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // In-place Modal State for Worksite
  const [siteModalOpen, setSiteModalOpen] = useState(false);
  const [siteFormData, setSiteFormData] = useState({ id: null, site_name: "" });

  // In-place Modal State for Task
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskFormData, setTaskFormData] = useState({
    id: null,
    worksite: null,
    task_name: "",
    deadline: "",
    progress: 0,
    rawProgress: {}
  });

  const projectState = useSelector((state) => state?.projectState);
  const companyState = useSelector((state) => state?.companyState);
  const worksiteallState = useSelector((state) => state?.worksiteallState);
  const user = useSelector((state) => state?.loginState?.auth);

  useEffect(() => {
    try {
      dispatch({ type: `${COMPANY}_GET`, payload: 0 });
    } catch (err) { }
  }, [dispatch]);

  useEffect(() => {
    setProjects(projectState?.data?.results || []);
  }, [projectState?.data?.results]);

  const fetchProgress = (id = 0) => {
    const url = `worksites/0/?filter_by={"project_id":"${id}"}`;
    dispatch({ type: `${WORKSITEALL}_GET`, payload: url });
  };

  const onCompanyChange = (value) => {
    setProjects([]);
    setProject({});
    if (value && value !== "0") {
      setCompany(value);
      dispatch({
        type: `${PROJECT}_GET`,
        payload: `project/0/?filter_by={"company":"${value}"}`,
      });
    } else {
      setCompany(0);
    }
  };

  const formatdate = (date) => (date ? String(date).split("T")[0] : "");

  const findStartEndDate = (prj, dayOffset = 0, size = windowSize) => {
    if (!prj || !prj.start_project_date) {
      const today = new Date();
      const s = new Date(today);
      s.setDate(s.getDate() + dayOffset);
      const e = new Date(s);
      e.setDate(e.getDate() + size - 1);
      return {
        startDate: s.toISOString(),
        endDate: e.toISOString(),
        formattedStartDate: formatdate(s.toISOString()),
        formattedEndDate: formatdate(e.toISOString()),
      };
    }
    const start = new Date(prj.start_project_date);
    start.setDate(start.getDate() + dayOffset);
    const end = new Date(start);
    end.setDate(end.getDate() + size - 1);

    const startDate = start.toISOString();
    const endDate = end.toISOString();
    return {
      startDate,
      endDate,
      formattedStartDate: formatdate(startDate),
      formattedEndDate: formatdate(endDate),
    };
  };

  const onProjectChange = (value) => {
    if (!value || value === "0") {
      setProject({});
      return;
    }
    const prjs = projectState?.data?.results?.filter((p) => String(p.id) === String(value));
    if (prjs?.[0]?.id) {
      const selectedPrj = prjs[0];
      setProject(selectedPrj);
      fetchProgress(selectedPrj.id);
      setDaysOffset(0);
      setDays(findStartEndDate(selectedPrj, 0, windowSize));
    }
  };

  useEffect(() => {
    if (from_project?.id) {
      setProject(from_project);
      fetchProgress(from_project.id);
      setDaysOffset(0);
      setDays(findStartEndDate(from_project, 0, windowSize));
      setCompany(from_project.company);
    }
  }, [from_project?.id]);

  // Recompute date window when offset or window size changes
  useEffect(() => {
    if (project?.id) {
      setDays(findStartEndDate(project, daysOffset, windowSize));
    }
  }, [daysOffset, windowSize, project]);

  const timelines = useMemo(() => {
    if (!days.formattedStartDate || !days.formattedEndDate) return [];
    const s = dayjs(days.formattedStartDate);
    const e = dayjs(days.formattedEndDate);
    const diff = Math.max(1, e.diff(s, "day") + 1);

    return Array.from({ length: diff }, (_, i) =>
      s.add(i, "day").format("YYYY-MM-DD")
    );
  }, [days.formattedStartDate, days.formattedEndDate]);

  // ================= Worksite Modal Handlers =================
  const handleOpenSiteModal = (siteToEdit = null) => {
    if (!project?.id) {
      Swal.fire({
        title: "Please Select a Project",
        text: "You must select a company and project before adding a worksite.",
        icon: "info",
        confirmButtonColor: "#3b82f6"
      });
      return;
    }
    if (siteToEdit) {
      setSiteFormData({ id: siteToEdit.id, site_name: siteToEdit.site_name || "" });
    } else {
      setSiteFormData({ id: null, site_name: "" });
    }
    setSiteModalOpen(true);
  };

  const handleSaveSite = (e) => {
    e.preventDefault();
    if (!siteFormData.site_name.trim() || !project?.id) return;

    if (siteFormData.id) {
      dispatch({
        type: `${WORKSITE}_PATCH`,
        payload: { id: siteFormData.id, site_name: siteFormData.site_name.trim(), project: project.id },
        callback: () => {
          fetchProgress(project.id);
          setSiteModalOpen(false);
        }
      });
    } else {
      dispatch({
        type: `${WORKSITE}_POST`,
        payload: { site_name: siteFormData.site_name.trim(), project: project.id },
        callback: () => {
          fetchProgress(project.id);
          setSiteModalOpen(false);
        }
      });
    }
  };

  const handleDeleteSite = (site) => {
    if (!site?.id || !project?.id) return;
    Swal.fire({
      title: `Delete Worksite "${site.site_name}"?`,
      text: "This will delete the worksite and all associated tasks. This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it!"
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch({
          type: `${WORKSITE}_DELETE`,
          payload: site.id,
          callback: () => {
            fetchProgress(project.id);
          }
        });
      }
    });
  };

  // ================= Task Modal Handlers =================
  const handleOpenTaskModal = (worksiteId, taskToEdit = null) => {
    if (!project?.id) return;
    if (taskToEdit) {
      setTaskFormData({
        id: taskToEdit.id,
        worksite: taskToEdit.worksite || worksiteId,
        task_name: taskToEdit.task_name || "",
        deadline: formatdate(taskToEdit.deadline || ""),
        progress: Number(taskToEdit.progress?.value || 0),
        rawProgress: taskToEdit.progress || {}
      });
    } else {
      setTaskFormData({
        id: null,
        worksite: worksiteId,
        task_name: "",
        deadline: formatdate(new Date().toISOString()),
        progress: 0,
        rawProgress: { value: 0 }
      });
    }
    setTaskModalOpen(true);
  };

  const handleSaveTask = (e) => {
    e.preventDefault();
    if (!taskFormData.task_name.trim() || !project?.id) return;

    const progressValue = Number(taskFormData.progress || 0);
    const progressObj = {
      ...(taskFormData.rawProgress || {}),
      value: progressValue
    };
    if (taskFormData.deadline) {
      progressObj[progressValue] = new Date(taskFormData.deadline).toISOString();
    }

    const payload = {
      task_name: taskFormData.task_name.trim(),
      deadline: taskFormData.deadline ? new Date(taskFormData.deadline).toISOString() : null,
      progress: progressObj,
      worksite: taskFormData.worksite
    };

    if (taskFormData.id) {
      payload.id = taskFormData.id;
      dispatch({
        type: `${PROGRESS}_PATCH`,
        payload,
        callback: () => {
          fetchProgress(project.id);
          setTaskModalOpen(false);
        }
      });
    } else {
      dispatch({
        type: `${PROGRESS}_POST`,
        payload,
        callback: () => {
          fetchProgress(project.id);
          setTaskModalOpen(false);
        }
      });
    }
  };

  const handleDeleteTask = (task) => {
    if (!task?.id || !project?.id) return;
    Swal.fire({
      title: `Delete Task "${task.task_name}"?`,
      text: "Are you sure you want to delete this task? This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it!"
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch({
          type: `${PROGRESS}_DELETE`,
          payload: task.id,
          callback: () => {
            fetchProgress(project.id);
          }
        });
      }
    });
  };

  // KPI Statistics across all tasks in all worksites
  const worksites = worksiteallState.data?.results || [];

  const allTasks = useMemo(() => {
    return worksites.flatMap((w) => w.tasks || []);
  }, [worksites]);

  const stats = useMemo(() => {
    let completed = 0;
    let inProgress = 0;
    let notStarted = 0;
    let overdue = 0;
    let totalProgressSum = 0;

    const today = dayjs().startOf("day");

    allTasks.forEach((t) => {
      const val = Number(t?.progress?.value || 0);
      totalProgressSum += val;
      if (val >= 100) {
        completed += 1;
      } else {
        if (t?.deadline) {
          const deadline = dayjs(t.deadline).startOf("day");
          if (deadline.isBefore(today)) {
            overdue += 1;
          } else if (val > 0) {
            inProgress += 1;
          } else {
            notStarted += 1;
          }
        } else if (val > 0) {
          inProgress += 1;
        } else {
          notStarted += 1;
        }
      }
    });

    const total = allTasks.length;
    const avgProgress = total > 0 ? Math.round(totalProgressSum / total) : 0;

    return { total, completed, inProgress, notStarted, overdue, avgProgress };
  }, [allTasks]);

  // Jump to today in timeline
  const handleJumpToToday = () => {
    if (!project?.start_project_date) return;
    const today = dayjs().startOf("day");
    const pStart = dayjs(project.start_project_date).startOf("day");
    const diff = today.diff(pStart, "day");
    const centeredOffset = Math.max(0, diff - Math.floor(windowSize / 3));
    setDaysOffset(centeredOffset);
  };

  // Filter worksites and tasks based on search and status
  const filteredWorksites = useMemo(() => {
    const today = dayjs().startOf("day");

    return worksites.map((site) => {
      const tasks = (site.tasks || []).filter((task) => {
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase();
          if (!task.task_name?.toLowerCase().includes(q)) return false;
        }
        const val = Number(task?.progress?.value || 0);
        const isDone = val >= 100;
        const isOverdue = !isDone && task.deadline && dayjs(task.deadline).startOf("day").isBefore(today);

        if (statusFilter === "completed") return isDone;
        if (statusFilter === "in_progress") return val > 0 && !isDone && !isOverdue;
        if (statusFilter === "overdue") return isOverdue;
        return true;
      });

      return {
        ...site,
        filteredTasks: tasks,
      };
    });
  }, [worksites, searchQuery, statusFilter]);

  return (
    <Container maxWidth="false" sx={{ py: 3, maxWidth: "1600px" }}>
      <ActionBar
        showGrid={[2, 5]}
        hasPermission={user?.permissions?.includes("work_progs.add_workprogress")}
        label="Task & Progress"
        btnLabel={"ADD NEW SITE"}
        setOpen={() => handleOpenSiteModal()}
      />

      {/* Project & Company Selector Bar */}
      <Card
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: "14px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          border: "1px solid #e2e8f0",
          background: "#ffffff",
        }}
      >
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
            {/* Company Select */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                background: "#f8fafc",
                p: "6px 12px",
                borderRadius: "10px",
                border: "1px solid #cbd5e1",
              }}
            >
              <BsBuilding color="#3b82f6" size={16} />
              <select
                onChange={(e) => onCompanyChange(e.target.value)}
                value={company}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#1e293b",
                  outline: "none",
                  cursor: "pointer",
                  paddingRight: "8px",
                }}
              >
                <option value="0">Select Company</option>
                {companyState?.data?.results?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_name}
                  </option>
                ))}
              </select>
            </Box>

            {/* Project Select */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                background: "#f8fafc",
                p: "6px 12px",
                borderRadius: "10px",
                border: "1px solid #cbd5e1",
              }}
            >
              <BsFolder2Open color="#f59e0b" size={16} />
              <select
                onChange={(e) => onProjectChange(e.target.value)}
                value={project?.id || "0"}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#1e293b",
                  outline: "none",
                  cursor: "pointer",
                  paddingRight: "8px",
                }}
              >
                <option value="0">Select Project</option>
                {projects?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.project_name}
                  </option>
                ))}
              </select>
            </Box>
          </Box>

          {/* Project Meta Information */}
          {project?.id && (
            <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
              <Box sx={{ fontSize: "12px", color: "#64748b" }}>
                <strong>Start:</strong> {dayjs(project.start_project_date).format("DD MMM YYYY")}
              </Box>
              <Box sx={{ fontSize: "12px", color: "#64748b" }}>
                <strong>End:</strong> {dayjs(project.end_project_date).format("DD MMM YYYY")}
              </Box>
              <Chip
                label={`${project.duration || 0} Days Total`}
                size="small"
                sx={{
                  backgroundColor: "rgba(59, 130, 246, 0.1)",
                  color: "#2563eb",
                  fontWeight: 600,
                  fontSize: "11px",
                }}
              />
            </Box>
          )}
        </Box>
      </Card>

      {/* Executive KPI Stats Dashboard */}
      {project?.id ? (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(5, 1fr)",
              },
              gap: 2,
              mb: 3,
            }}
          >
            {/* Total Tasks */}
            <Card
              sx={{
                p: 2,
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography fontSize={12} color="#64748b" fontWeight={600}>
                  Total Tasks
                </Typography>
                <BsListTask color="#64748b" size={18} />
              </Box>
              <Typography fontSize={24} fontWeight={700} color="#0f172a" mt={0.5}>
                {stats.total}
              </Typography>
              <Typography fontSize={11} color="#94a3b8">
                Across {worksites.length} sites
              </Typography>
            </Card>

            {/* Overall Progress */}
            <Card
              sx={{
                p: 2,
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography fontSize={12} color="#64748b" fontWeight={600}>
                  Overall Progress
                </Typography>
                <Typography fontSize={14} fontWeight={700} color="#2563eb">
                  {stats.avgProgress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={stats.avgProgress}
                sx={{
                  my: 1.5,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#e2e8f0",
                  "& .MuiLinearProgress-bar": {
                    background: "linear-gradient(90deg, #3b82f6, #10b981)",
                    borderRadius: 4,
                  },
                }}
              />
              <Typography fontSize={11} color="#64748b">
                {stats.completed} of {stats.total} tasks completed
              </Typography>
            </Card>

            {/* Completed Tasks */}
            <Card
              sx={{
                p: 2,
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
                background: "linear-gradient(180deg, #ffffff 0%, rgba(240, 253, 244, 0.5) 100%)",
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography fontSize={12} color="#15803d" fontWeight={600}>
                  Completed
                </Typography>
                <BsCheckCircleFill color="#16a34a" size={18} />
              </Box>
              <Typography fontSize={24} fontWeight={700} color="#166534" mt={0.5}>
                {stats.completed}
              </Typography>
              <Typography fontSize={11} color="#16a34a">
                {stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}% done
              </Typography>
            </Card>

            {/* In Progress */}
            <Card
              sx={{
                p: 2,
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
                background: "linear-gradient(180deg, #ffffff 0%, rgba(239, 246, 255, 0.5) 100%)",
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography fontSize={12} color="#1d4ed8" fontWeight={600}>
                  In Progress
                </Typography>
                <BsClockHistory color="#3b82f6" size={18} />
              </Box>
              <Typography fontSize={24} fontWeight={700} color="#1e40af" mt={0.5}>
                {stats.inProgress}
              </Typography>
              <Typography fontSize={11} color="#3b82f6">
                Actively advancing
              </Typography>
            </Card>

            {/* Overdue / Critical */}
            <Card
              sx={{
                p: 2,
                borderRadius: "12px",
                border: "1px solid",
                borderColor: stats.overdue > 0 ? "#fca5a5" : "#e2e8f0",
                boxShadow: stats.overdue > 0 ? "0 4px 14px rgba(239, 68, 68, 0.12)" : "0 2px 10px rgba(0,0,0,0.03)",
                background: stats.overdue > 0 ? "linear-gradient(180deg, #ffffff 0%, #fef2f2 100%)" : "#ffffff",
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography fontSize={12} color={stats.overdue > 0 ? "#b91c1c" : "#64748b"} fontWeight={600}>
                  Overdue Tasks
                </Typography>
                <BsExclamationTriangleFill color={stats.overdue > 0 ? "#ef4444" : "#94a3b8"} size={18} />
              </Box>
              <Typography fontSize={24} fontWeight={700} color={stats.overdue > 0 ? "#dc2626" : "#64748b"} mt={0.5}>
                {stats.overdue}
              </Typography>
              <Typography fontSize={11} color={stats.overdue > 0 ? "#ef4444" : "#94a3b8"}>
                {stats.overdue > 0 ? "Requires immediate attention" : "No overdue tasks"}
              </Typography>
            </Card>
          </Box>

          {/* Timeline Navigation & Filter Bar */}
          <Card
            sx={{
              p: 2,
              mb: 3,
              borderRadius: "14px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
              background: "#ffffff",
            }}
          >
            <Box
              display="flex"
              flexWrap="wrap"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              mb={2}
            >
              {/* Left: Search & Filter Pills */}
              <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5}>
                {/* Search box */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    background: "#f8fafc",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    px: 1.5,
                    py: 0.5,
                  }}
                >
                  <BsSearch color="#94a3b8" size={13} />
                  <input
                    type="text"
                    placeholder="Search tasks..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      border: "none",
                      background: "transparent",
                      fontSize: "12px",
                      outline: "none",
                      width: "150px",
                      color: "#1e293b",
                    }}
                  />
                </Box>

                {/* Filter Pills */}
                <Chip
                  label="All Tasks"
                  size="small"
                  clickable
                  onClick={() => setStatusFilter("all")}
                  sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    backgroundColor: statusFilter === "all" ? "#1e293b" : "#f1f5f9",
                    color: statusFilter === "all" ? "#ffffff" : "#475569",
                  }}
                />
                <Chip
                  label="In Progress"
                  size="small"
                  clickable
                  onClick={() => setStatusFilter("in_progress")}
                  sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    backgroundColor: statusFilter === "in_progress" ? "#2563eb" : "#eff6ff",
                    color: statusFilter === "in_progress" ? "#ffffff" : "#1d4ed8",
                  }}
                />
                <Chip
                  label="Completed"
                  size="small"
                  clickable
                  onClick={() => setStatusFilter("completed")}
                  sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    backgroundColor: statusFilter === "completed" ? "#16a34a" : "#f0fdf4",
                    color: statusFilter === "completed" ? "#ffffff" : "#15803d",
                  }}
                />
                <Chip
                  label={`Overdue (${stats.overdue})`}
                  size="small"
                  clickable
                  onClick={() => setStatusFilter("overdue")}
                  sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    backgroundColor: statusFilter === "overdue" ? "#dc2626" : "#fef2f2",
                    color: statusFilter === "overdue" ? "#ffffff" : "#b91c1c",
                  }}
                />
              </Box>

              {/* Right: Date Window Controllers */}
              <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
                {/* Window Size Options */}
                <Box sx={{ display: "flex", background: "#f1f5f9", borderRadius: "8px", p: "2px" }}>
                  {[14, 21, 30].map((sz) => (
                        <button
                          key={sz}
                          onClick={() => setWindowSize(sz)}
                          style={{
                            border: "none",
                            background: windowSize === sz ? "#ffffff" : "transparent",
                            color: windowSize === sz ? "#1e293b" : "#64748b",
                            fontWeight: windowSize === sz ? 700 : 500,
                            borderRadius: "6px",
                            padding: "4px 8px",
                            fontSize: "11px",
                            cursor: "pointer",
                            boxShadow: windowSize === sz ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                          }}
                        >
                          {sz}D
                        </button>
                  ))}
                </Box>

                {/* Navigation Buttons */}
                <IconButton
                  size="small"
                  onClick={() => setDaysOffset((prev) => Math.max(0, prev - windowSize))}
                  disabled={daysOffset <= 0}
                  sx={{
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    p: 0.6,
                    "&:hover": { backgroundColor: "#f1f5f9" },
                  }}
                >
                  <BsChevronLeft size={12} />
                </IconButton>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleJumpToToday}
                  startIcon={<BsCalendar3 size={11} />}
                  sx={{
                    textTransform: "none",
                    fontSize: "11px",
                    fontWeight: 600,
                    py: 0.4,
                    px: 1.2,
                    borderRadius: "8px",
                    borderColor: "#3b82f6",
                    color: "#2563eb",
                    "&:hover": { backgroundColor: "rgba(59, 130, 246, 0.08)" },
                  }}
                >
                  Today
                </Button>

                <IconButton
                  size="small"
                  onClick={() => setDaysOffset((prev) => prev + windowSize)}
                  sx={{
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    p: 0.6,
                    "&:hover": { backgroundColor: "#f1f5f9" },
                  }}
                >
                  <BsChevronRight size={12} />
                </IconButton>
              </Box>
            </Box>

            {/* Window Range & Slider */}
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography fontSize={11} color="#64748b">
                  Project Start: <strong>{dayjs(project.start_project_date).format("DD MMM YYYY")}</strong>
                </Typography>
                <Chip
                  label={`Showing: ${dayjs(days.formattedStartDate).format("DD MMM YYYY")} – ${dayjs(days.formattedEndDate).format("DD MMM YYYY")} (${windowSize} Days)`}
                  size="small"
                  sx={{
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    fontWeight: 600,
                    fontSize: "11px",
                    color: "#1e293b",
                  }}
                />
                <Typography fontSize={11} color="#64748b">
                  Project End: <strong>{dayjs(project.end_project_date).format("DD MMM YYYY")}</strong>
                </Typography>
              </Box>
              <input
                type="range"
                min="0"
                step={1}
                max={Number(project?.duration || 60)}
                value={daysOffset}
                onChange={(e) => setDaysOffset(Number(e.target.value))}
                style={{
                  width: "100%",
                  accentColor: "#3b82f6",
                  cursor: "pointer",
                  height: "6px",
                  borderRadius: "4px",
                }}
              />
            </Box>
          </Card>

          {/* Worksites List & Gantt Tables */}
          <Box>
            {filteredWorksites.map((work) => {
              return (
                <Card
                  key={work.id}
                  className="timeline-card"
                  sx={{
                    mb: 3,
                    borderRadius: "14px",
                    border: "1px solid #e2e8f0",
                    overflow: "hidden",
                  }}
                >
                  {/* Worksite Header */}
                  <Box
                    sx={{
                      p: "14px 20px",
                      background: "linear-gradient(90deg, #f8fafc 0%, #ffffff 100%)",
                      borderBottom: "1px solid #e2e8f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1.5,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Typography fontSize={15} fontWeight={700} color="#0f172a">
                        {work.site_name}
                      </Typography>
                      <Tooltip title="Edit Worksite Name">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenSiteModal(work)}
                          sx={{
                            color: "#94a3b8",
                            p: 0.5,
                            "&:hover": { color: "#2563eb", backgroundColor: "rgba(37,99,235,0.1)" },
                          }}
                        >
                          <BsFillPencilFill size={11} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Worksite">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSite(work)}
                          sx={{
                            color: "#94a3b8",
                            p: 0.5,
                            "&:hover": { color: "#ef4444", backgroundColor: "rgba(239,68,68,0.1)" },
                          }}
                        >
                          <BsTrash3 size={12} />
                        </IconButton>
                      </Tooltip>
                      <Chip
                        label={`${work.tasks?.length || 0} Tasks`}
                        size="small"
                        sx={{
                          backgroundColor: "#f1f5f9",
                          color: "#475569",
                          fontWeight: 600,
                          fontSize: "11px",
                          height: "22px",
                        }}
                      />
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<BsPlusLg size={11} />}
                        onClick={() => handleOpenTaskModal(work.id)}
                        sx={{
                          borderRadius: "8px",
                          fontSize: "11px",
                          fontWeight: 600,
                          textTransform: "none",
                          backgroundColor: "#2563eb",
                          boxShadow: "none",
                          py: 0.5,
                          px: 1.5,
                          "&:hover": { backgroundColor: "#1d4ed8" },
                        }}
                      >
                        Add New Task
                      </Button>
                    </Box>
                  </Box>

                  {/* Worksite Tasks Table / Gantt View */}
                  {work.filteredTasks?.length === 0 ? (
                    <Box
                      sx={{
                        p: 4,
                        textAlign: "center",
                        background: "#ffffff",
                      }}
                    >
                      <Typography fontSize={13} color="#94a3b8" mb={1.5}>
                        {work.tasks?.length === 0
                          ? "No tasks have been added to this site yet."
                          : "No tasks matching the selected filters."}
                      </Typography>
                      {work.tasks?.length === 0 && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<BsPlusLg size={11} />}
                          onClick={() => handleOpenTaskModal(work.id)}
                          sx={{
                            textTransform: "none",
                            fontSize: "12px",
                            borderRadius: "8px",
                          }}
                        >
                          Add First Task
                        </Button>
                      )}
                    </Box>
                  ) : (
                    <Box className="timeline-scroll-container">
                      <table className="timeline-table">
                        <thead>
                          <tr>
                            <th style={{ width: "220px", textAlign: "left", paddingLeft: "14px" }}>
                              Task Name
                            </th>
                            <th style={{ width: "170px" }}>Deadline</th>
                            <th style={{ width: "130px" }}>Progress</th>
                            {timelines.map((d, i) => {
                              const isToday = dayjs(d).isSame(dayjs(), "day");
                              const isWeekend = dayjs(d).day() === 0 || dayjs(d).day() === 6;
                              return (
                                <th
                                  key={i}
                                  className={`timeline-date-th ${isToday ? "today-col" : ""} ${
                                    isWeekend ? "weekend-col" : ""
                                  }`}
                                >
                                  <div className="th-day">{dayjs(d).format("ddd")}</div>
                                  <div className="th-date">{dayjs(d).format("DD")}</div>
                                  <div className="th-month">{dayjs(d).format("MMM")}</div>
                                  {isToday && <div className="today-badge">TODAY</div>}
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {work.filteredTasks.map((item) => (
                            <Timeline
                              key={item.id}
                              item={item}
                              days={timelines}
                              timelineStart={days.formattedStartDate}
                              onProgressClick={(task) => handleOpenTaskModal(work.id, task)}
                              onTaskDelete={(task) => handleDeleteTask(task)}
                              project={project}
                            />
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  )}
                </Card>
              );
            })}

            {/* Empty State: No Worksites in Project */}
            {worksites.length === 0 && (
              <Card
                sx={{
                  p: 6,
                  textAlign: "center",
                  borderRadius: "14px",
                  border: "1px dashed #cbd5e1",
                  background: "#f8fafc",
                }}
              >
                <Typography fontSize={16} fontWeight={600} color="#334155" mb={1}>
                  No worksites found in this project
                </Typography>
                <Typography fontSize={13} color="#64748b" mb={3}>
                  Get started by adding a worksite to organize tasks and track progress.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => handleOpenSiteModal()}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    backgroundColor: "#2563eb",
                    borderRadius: "8px",
                    px: 3,
                  }}
                >
                  + Add New Site
                </Button>
              </Card>
            )}
          </Box>
        </>
      ) : (
        /* Prompt to select project */
        <Card
          sx={{
            p: 8,
            textAlign: "center",
            borderRadius: "16px",
            border: "1px dashed #cbd5e1",
            background: "#f8fafc",
          }}
        >
          <BsFolder2Open size={48} color="#94a3b8" />
          <Typography fontSize={18} fontWeight={700} color="#1e293b" mt={2} mb={1}>
            Select a Project to View Progress
          </Typography>
          <Typography fontSize={13} color="#64748b" maxWidth={450} mx="auto">
            Choose a company and project from the dropdown menus above to monitor timelines, deadlines, and task completion.
          </Typography>
        </Card>
      )}

      {/* ================= IN-PLACE WORKSITE MODAL POPUP ================= */}
      <Dialog
        open={siteModalOpen}
        onClose={() => setSiteModalOpen(false)}
        maxWidth="xs"
        fullWidth
        BackdropProps={{
          className: "task-modal-backdrop"
        }}
        PaperProps={{
          className: "task-modal-paper",
          sx: { borderRadius: "16px", p: 1 }
        }}
      >
        <DialogTitle className="task-modal-title" sx={{ fontWeight: 700, fontSize: 17, pb: 0.5 }}>
          {siteFormData.id ? "Edit Worksite" : "Add New Worksite"}
        </DialogTitle>
        <form onSubmit={handleSaveSite}>
          <DialogContent sx={{ pt: 1 }}>
            <Typography fontSize={12} color="#64748b" mb={2}>
              {siteFormData.id
                ? "Update the name of this worksite."
                : `Enter a name to create a new worksite under "${project?.project_name || 'Project'}".`}
            </Typography>
            <Box mb={1}>
              <label className="input-label">Worksite Name</label>
              <input
                type="text"
                autoFocus
                placeholder="Worksite Name"
                value={siteFormData.site_name}
                onChange={(e) => setSiteFormData({ ...siteFormData, site_name: e.target.value })}
                required
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setSiteModalOpen(false)}
              sx={{ textTransform: "none", color: "#64748b", fontWeight: 500 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!siteFormData.site_name.trim()}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: "#2563eb",
                borderRadius: "8px",
                px: 2.5
              }}
            >
              {siteFormData.id ? "Update Site" : "Add Site"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ================= IN-PLACE TASK MODAL POPUP ================= */}
      <Dialog
        open={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        maxWidth="sm"
        fullWidth
        BackdropProps={{
          className: "task-modal-backdrop"
        }}
        PaperProps={{
          className: "task-modal-paper",
          sx: { borderRadius: "16px", p: 1 }
        }}
      >
        <DialogTitle className="task-modal-title" sx={{ fontWeight: 700, fontSize: 17, pb: 0.5 }}>
          {taskFormData.id ? "Edit Task" : "Add New Task"}
        </DialogTitle>
        <form onSubmit={handleSaveTask}>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Box>
              <label className="input-label">Task Name</label>
              <input
                type="text"
                autoFocus
                placeholder="Task Name"
                value={taskFormData.task_name}
                onChange={(e) => setTaskFormData({ ...taskFormData, task_name: e.target.value })}
                required
              />
            </Box>

            <Box>
              <label className="input-label">Deadline Date</label>
              <input
                type="date"
                value={taskFormData.deadline}
                onChange={(e) => setTaskFormData({ ...taskFormData, deadline: e.target.value })}
                min={formatdate(project?.start_project_date)}
                max={formatdate(project?.end_project_date)}
              />
              {project?.start_project_date && (
                <Typography fontSize={11} color="#64748b" mt={0.5}>
                  Project duration: {formatdate(project?.start_project_date)} to {formatdate(project?.end_project_date)}
                </Typography>
              )}
            </Box>

            <Box className="progress-box" sx={{ p: 2, borderRadius: "10px" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography fontSize={13} fontWeight={600} className="input-label" sx={{ mb: 0 }}>
                  Completion Progress
                </Typography>
                <Chip
                  label={`${taskFormData.progress}%`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    backgroundColor:
                      taskFormData.progress >= 100
                        ? "rgba(16, 185, 129, 0.15)"
                        : taskFormData.progress > 0
                        ? "rgba(59, 130, 246, 0.15)"
                        : "#f1f5f9",
                    color:
                      taskFormData.progress >= 100
                        ? "#10b981"
                        : taskFormData.progress > 0
                        ? "#2563eb"
                        : "#64748b"
                  }}
                />
              </Box>

              <Slider
                value={taskFormData.progress}
                min={0}
                max={100}
                step={5}
                onChange={(_, val) => setTaskFormData({ ...taskFormData, progress: val })}
                sx={{
                  color: taskFormData.progress >= 100 ? "#10b981" : "#2563eb",
                  height: 6
                }}
              />

              <Box display="flex" justifyContent="space-between" gap={1} mt={1}>
                {[0, 25, 50, 75, 100].map((pct) => (
                  <Button
                    key={pct}
                    size="small"
                    variant={taskFormData.progress === pct ? "contained" : "outlined"}
                    onClick={() => setTaskFormData({ ...taskFormData, progress: pct })}
                    sx={{
                      flex: 1,
                      py: 0.3,
                      fontSize: 11,
                      fontWeight: 600,
                      borderRadius: "6px",
                      borderColor: "#cbd5e1",
                      color: taskFormData.progress === pct ? "#ffffff" : "#475569",
                      backgroundColor:
                        taskFormData.progress === pct
                          ? pct === 100
                            ? "#10b981"
                            : "#2563eb"
                          : "transparent"
                    }}
                  >
                    {pct}%
                  </Button>
                ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setTaskModalOpen(false)}
              sx={{ textTransform: "none", color: "#64748b", fontWeight: 500 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!taskFormData.task_name.trim()}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: "#2563eb",
                borderRadius: "8px",
                px: 2.5
              }}
            >
              {taskFormData.id ? "Update Task" : "Add Task"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
}
